import { toast, ToastContent, ToastOptions } from 'react-toastify';
import { TFunction } from 'i18next';

import { Profile } from '@/api/login/types.ts';
import { CandidateApi, CandidateUI } from '@/api/matchmaker/types.ts';
import {
  I18NLanguageCodes,
  i18nLanguageCodes,
  TgLanguageCodes
} from '@/constants/tg-language-codes.ts';
import {
  PhotoKind,
  PhotoVariants,
  VideoKind,
  VideoVariants
} from '@/shared/type/userDataFields.ts';
import { UserLabels, UserStates } from '@/enums/apiEnums.ts';
import { routes } from '@/navigation/routes.tsx';
import { MessageTypesUI } from '@/enums/viewEnums.ts';

interface ToastParams {
  id: MessageTypesUI;
  content: ToastContent<unknown>;
  exclude?: string[];
  options?: ToastOptions<unknown>;
}

export const helpers = {
  checkRegistrationRoute: (profile?: Profile): string => {
    if (!profile) {
      return routes.registrationUserData;
    }

    switch (true) {
      case !profile.birthday:
      case !profile.gender:
        return routes.registrationWelcomePage;
      case !profile.v2photos.length:
        return routes.registrationUserPhotos;
      case profile.v2photos.length > 0:
      case !!profile.gender:
      case !!profile.birthday:
        return routes.registrationUserSelfie;
      default:
        break;
    }

    return routes.registrationUserData;
  },
  mapToDiscoveryModel: (v: CandidateApi): CandidateUI => {
    const activityLabels: (UserStates | UserLabels)[] = [];

    if (v.labels.includes(UserLabels.LABEL_LIKES_ME)) {
      activityLabels.push(UserLabels.LABEL_LIKES_ME);
    } else if (v.profile.online_state === UserStates.ONLINE_STATE_ONLINE) {
      activityLabels.push(UserStates.ONLINE_STATE_ONLINE);
    } else if (v.labels.includes(UserLabels.LABEL_JUST_JOINED)) {
      activityLabels.push(UserLabels.LABEL_JUST_JOINED);
    }

    return {
      isActive: v.profile.online_state === UserStates.ONLINE_STATE_ONLINE,
      isJustJoined: v.labels.includes(UserLabels.LABEL_JUST_JOINED),
      likesMe: v.labels.includes(UserLabels.LABEL_LIKES_ME),
      activityLabels,
      profile: {
        hid: v.profile.hid,
        name: v.profile.name,
        age: v.profile.age,
        gender: v.profile.gender,
        geoProximity: v.distance,
        aboutMe: v.profile.about_me ?? '',
        bodyType: v.profile.body_type,
        smokingStatus: v.profile.smoking_status,
        alcoholPreference: v.profile.alcohol_preference,
        zodiacSign: v.profile.zodiac_sign,
        employmentSector: v.profile.employment_sector ?? '',
        heightCm: v.profile.height_cm,
        v2photos: v.profile.v2photos,
        v2videos: v.profile.v2videos,
        cityName: v.profile.city?.name
      }
    };
  },
  executeWithDelay: async <T>(cb: () => Promise<T>, delay: number) => {
    await new Promise((resolve) => setTimeout(resolve, delay));
    return await cb();
  },
  convertToI18nLanguageCode: (tgLang: TgLanguageCodes): I18NLanguageCodes =>
    tgLang in i18nLanguageCodes ? i18nLanguageCodes[tgLang] : 'en-US',
  calculateAge: (birthdate: string): number => {
    const birthDate = new Date(birthdate);
    const currentDate = new Date();

    let age = currentDate.getFullYear() - birthDate.getFullYear();
    const monthDifference = currentDate.getMonth() - birthDate.getMonth();
    const dayDiff = currentDate.getDate() - birthDate.getDate();

    if (monthDifference < 0 || (monthDifference === 0 && dayDiff < 0)) {
      age--;
    }

    return age;
  },
  calculateAgeRange: (
    age: number
  ): { age_filter_min: number; age_filter_max: number } => {
    if (age < 30) {
      return { age_filter_min: 18, age_filter_max: 35 };
    } else if (age < 40) {
      return { age_filter_min: 18, age_filter_max: 50 };
    } else {
      return { age_filter_min: 18, age_filter_max: 60 };
    }
  },
  getDifferingFields: <
    T extends Record<string, unknown>,
    U extends Record<string, unknown>
  >(
    initial: T,
    updated: U
  ): { [K in keyof T & keyof U]: U[K] } => {
    const commonKeys = Object.keys(initial).filter((key) => key in updated);

    return commonKeys.reduce(
      (diff, key) => {
        if (initial[key] !== updated[key]) {
          (diff as Record<string, unknown>)[key] = updated[key];
        }

        return diff;
      },
      {} as { [K in keyof T & keyof U]: U[K] }
    );
  },
  formatDate: (
    date: Date,
    format: 'YYYY-MM-DD HH:mm:ss' | 'DD-MM-YYYY' = 'YYYY-MM-DD HH:mm:ss'
  ) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    const formatMap: Record<string, string> = {
      YYYY: year.toString(),
      MM: month,
      DD: day,
      HH: hours,
      mm: minutes,
      ss: seconds
    };

    return format.replace(/YYYY|MM|DD|HH|mm|ss/g, (match) => formatMap[match]);
  },
  getPublicUrl: (path: string) => {
    let baseUrl = import.meta.env.BASE_URL;
    if (!baseUrl.endsWith('/')) {
      baseUrl += '/';
    }

    let isBaseAbsolute = false;
    try {
      new URL(baseUrl);
      isBaseAbsolute = true;
    } catch {
      /* empty */
    }

    return new URL(
      path.replace(/^\/+/, ''),
      isBaseAbsolute ? baseUrl : window.location.origin + baseUrl
    ).toString();
  },
  toast: ({ id, content, exclude = [], options }: ToastParams) => {
    const pathname = window.location.pathname;

    const isExcluded = exclude.some((pattern) => {
      if (pattern.endsWith('/*')) {
        const base = pattern.replace('/*', '');
        return pathname === base || pathname.startsWith(`${base}/`);
      }

      return pathname === pattern;
    });

    if (!isExcluded && !toast.isActive(id)) {
      toast(content, {
        toastId: id,
        style: { background: 'transparent', boxShadow: 'none' },
        ...options
      });
    }
  },
  generateUUID: () => {
    const template = '10000000-1000-4000-8000-100000000000';

    const generate = (char: string): string => {
      const randomByte = crypto.getRandomValues(new Uint8Array(1))[0];
      const c = Number(char);

      return (c ^ (randomByte & (15 >> (c / 4)))).toString(16);
    };

    return template.replace(/[018]/g, generate);
  },
  getDeviceId: function () {
    let deviceID = localStorage.getItem('deviceID');

    if (!deviceID) {
      deviceID = this.generateUUID();
      localStorage.setItem('deviceID', deviceID);
    }

    return deviceID;
  },
  convertMicrosToTON: (
    micros: number | string | undefined,
    toFixed: number = 4
  ): string => {
    if (micros === undefined || micros === null || micros === '') return '0';

    const value = typeof micros === 'string' ? parseFloat(micros) : micros;
    if (isNaN(value)) return '0';

    const tons = value / 1_000_000;
    return tons === 0 ? '0' : tons.toFixed(toFixed);
  },
  convertTONToMicros: (ton: number | string | undefined): number => {
    if (ton === undefined || ton === null || ton === '') return 0;

    const value = typeof ton === 'string' ? parseFloat(ton) : ton;
    if (isNaN(value)) return 0;

    return value * 1_000_000;
  },
  convertCentsToUSD: (cents: number | undefined): string => {
    if (!cents && cents !== 0) return '0';
    const dollars = cents / 100;

    return dollars === 0 ? '0' : dollars.toFixed(2);
  },
  getDistance: (geoProximity: number, t: TFunction): string => {
    if (!geoProximity || geoProximity === 0) return t('ukraine');
    if (geoProximity <= 1) return t('geolocation.near_you');
    if (geoProximity > 1 && geoProximity <= 100)
      return t('geolocation.km_away', { distance: geoProximity });

    return t('ukraine');
  },
  getEnvVar(key: string, defaultValue: string): string {
    return (import.meta.env[key] as string | undefined) ?? defaultValue;
  },
  generateRequestId() {
    return Math.random().toString(36).substring(2, 9);
  },
  getPrimaryPhoto: (photos: PhotoVariants[]) => {
    return photos.find((p) => p.kind === PhotoKind.KIND_PRIMARY)?.url;
  },
  getThumbPhoto: (photos: PhotoVariants[]) => {
    return photos.find((p) => p.kind === PhotoKind.KIND_THUMB)?.url;
  },
  getThumbHash: (variants: PhotoVariants[]): string | undefined => {
    const primary = variants.find((v) => v.kind === PhotoKind.KIND_PRIMARY);
    if (primary?.thumbhash) return primary.thumbhash;
    return variants.find((v) => !!v.thumbhash)?.thumbhash;
  },
  getVideoUrl: (variants: VideoVariants[]) => {
    return variants?.find((v) => v.kind === VideoKind.KIND_VIDEO)?.url;
  },
  getVideoPoster: (variants: VideoVariants[]) => {
    return variants?.find((v) => v.kind === VideoKind.KIND_POSTER)?.url;
  },
  getVideoThumbHash: (variants: VideoVariants[]): string | undefined => {
    const poster = variants?.find((v) => v.kind === VideoKind.KIND_POSTER);
    return poster?.thumbhash;
  }
};
